import { useCallback, useState } from 'react';
import { GoogleMap, useJsApiLoader, Autocomplete, DirectionsRenderer, OverlayView } from '@react-google-maps/api'
import Loading from '../loading/loading';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import MapFilter from './mapFilter';
import MapResults from './mapResults';
import { GetCalculatorInfo } from '../../context/calculator/tour/calculatorProvider';
import { GetPastTrips } from '../../context/trip/tripProvider';

/*
 * Google map examples: https://github.com/JustFly1984/react-google-maps-api/tree/master/packages/react-google-maps-api-gatsby-example/src/examples
*/

const containerStyle = {
    width: '100%',
    height: '100%',
}

// Katunayake Airport
const center = {
    lat: 7.1802,
    lng: 79.8842
}

const GoogleMapView = () => {
    const theme = useTheme()

    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP,
        libraries: ['places']
    });

    const [directionResponse, setDirectionResponse] = useState(null);
    const [results, setResults] = useState({
        distance: '',
        elapsedTime: ''
    });

    const [map, setMap] = useState(/** @type google.maps.Map */ (null));

    const calculator = GetCalculatorInfo(); 
    const trip = GetPastTrips();

    const onLoad = useCallback((map) => {
        setMap(map);
    }, []);

    const onUnmount = useCallback((map) => {
        setMap(null);
    }, []);

    const getBackToCenter = useCallback(() => {
        map.panTo(center);
    }, [map, center]);

    const clearRoutes = useCallback(() => {
        setDirectionResponse(null);

        setResults({
            distance: '',
            elapsedTime: '' 
        });
    }, []);

    const calculateRoute = useCallback(async (destinations) => {
        let validWayPoints = [];
        let coordinatesObject = [];

        for(let i = 0; i < destinations.length; i++){
            if(destinations[i].lat != -1 && destinations[i].lng != -1){
                validWayPoints.push(destinations[i]);
                coordinatesObject.push(destinations[i]);
            }
        }

        if(validWayPoints.length < 2){
            console.log("Not enough coordinates to render the map...");
            console.log(destinations);
            console.log(validWayPoints);

            clearRoutes();

            return;
        }

        console.log(destinations);
        console.log(coordinatesObject);
        calculator.updateCoordinates(coordinatesObject);

        console.log("Has enough coordinates to render the map...");
        console.log(validWayPoints);

        const originDestination = validWayPoints[0];
        const finalDestination = validWayPoints[validWayPoints.length - 1];

        const waypoints = validWayPoints.slice(0, -1).map((location) => ({
            // eslint-disable-next-line no-undef
            location: new google.maps.LatLng(location.lat, location.lng),
            stopover: true,
        }));

        console.log("Origin destination's lat: " + originDestination.lat + ", lng: " + originDestination.lng);
        console.log("Final destination's lat: " + finalDestination.lat + ", lng: " + finalDestination.lng);

        console.log("Way points:");
        console.log(waypoints);

        // eslint-disable-next-line no-undef
        const directionService = new google.maps.DirectionsService();

        const results = await directionService.route({
            // eslint-disable-next-line no-undef
            origin: new google.maps.LatLng(originDestination.lat, originDestination.lng),
            // eslint-disable-next-line no-undef
            destination: new google.maps.LatLng(finalDestination.lat, finalDestination.lng),
            waypoints: waypoints,
            // eslint-disable-next-line no-undef
            travelMode: google.maps.TravelMode.DRIVING,
            // drivingOptions: {
            //     departureTime: new Date(), // or a Date in the future
            //     trafficModel: 'optimistic'  // 'bestguess' | 'pessimistic' | 'optimistic'
            // },
            optimizeWaypoints: false
        });

        console.log(results);

        setDirectionResponse(results);

        const leg = results.routes[0].legs;
        const totalDistance = leg.reduce((sum, l) => sum + l.distance.value, 0); // in meters
        const totalDuration = leg.reduce((sum, l) => sum + l.duration.value, 0); // in seconds

        console.log(leg);

        console.log("Total distance: " + totalDistance + " meters");
        console.log("Total duration: " + totalDuration + " seconds");

        const distance = (totalDistance / 1000).toFixed(2);

        const hrs = Math.floor(totalDuration / 3600);
        const remainingSeconds = totalDuration - (hrs * 3600);
        const min = Math.round(remainingSeconds / 60);

        setResults({
            distance: `${distance} km`,
            elapsedTime: hrs > 0 ? `${hrs} hr ${min} min` : `${min} min`,
        });

        calculator.updateMapData(distance, hrs, min);

        trip.updateDistance(distance);
        trip.updateTime({hour: hrs, minute: min});
        trip.updateCoordinates(coordinatesObject);
    }, [calculator, trip]);

    return (
        <>
            {
                isLoaded
                ?
                <Box
                    position='absolute'
                    top={0}
                    left={0}
                    width='100%'
                    height='100vh'
                    zIndex={-1}
                >
                    <GoogleMap
                        mapContainerStyle={containerStyle}
                        center={center}
                        zoom={15}
                        onLoad={onLoad}
                        onUnmount={onUnmount}
                        options={{
                            mapTypeControl: false,
                            streetViewControl: false,
                            fullscreenControl: false
                        }}
                    >
                        <Stack
                            direction='column'
                            justifyContent='start'
                            alignItems='start'
                            sx={{
                                position: 'absolute',
                                ml: {xs: 0, lg: 15},
                                mt: {xs: 0, lg: 6}
                            }}
                            spacing={2}
                        >
                            <MapFilter
                                Autocomplete={Autocomplete}
                                calculateRoute={calculateRoute}
                                clearRoutes={clearRoutes}
                            />

                            {
                                results.distance && results.elapsedTime
                                ?
                                <MapResults results={results}/>
                                :
                                <></>
                            }
                        </Stack>

                        {directionResponse && (
                            <>
                                <DirectionsRenderer 
                                    directions={directionResponse} 
                                    options={{
                                        polylineOptions: {
                                            strokeColor: theme.palette.primary.main,
                                            strokeWeight: 5,
                                            strokeOpacity: 1
                                        },
                                        suppressMarkers: true
                                    }}
                                />

                                {
                                    directionResponse.routes[0].legs.map((item, index) => {
                                        return <CustomMarker 
                                            key={index}
                                            position={{ lat: item.end_location.lat(), lng: item.end_location.lng() }} 
                                            index={index}
                                            destinationIndex={directionResponse.routes[0].legs.length - 1}
                                        />
                                    })
                                }
                            </>
                        )}
                    </GoogleMap>
                </Box>
                :
                <Loading label='Map is loading...'/>
            }
        </>
    );
}

export default GoogleMapView;

export const CustomMarker = ({ position, index, destinationIndex }) => {
  return (
    <OverlayView
      position={position}
      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
    >
        <>
            <Stack
                position='relative'
                direction='row'
                justifyContent='center'
                alignItems='center'
            >
                <img 
                    src={index === destinationIndex ? '/images/placeholder.png' : '/logo512.png'} 
                    alt='waypoint'
                    width='30px'
                    style={{
                        borderRadius: '50%',
                        border: index === destinationIndex ? 'none' : '2px solid white'
                    }}
                />

                {
                    index === destinationIndex
                    ?
                    <></>
                    :
                    <Typography
                        textAlign="center"
                        fontWeight={700}
                        fontSize={14}
                        color='white'
                        position='absolute'
                        style={{
                            textShadow: '1px 1px #000000'
                        }}
                    >
                        {index + 1 < 10 ? `0${index + 1}` : index + 1}
                    </Typography>
                }
            </Stack>
        </>
    </OverlayView>
  );
};